// Who has an item (docs/design/actions.md, A refined; several people: OEM-285): a stack of small avatars
// at the end of the card's head row (up to 3, then "+n"), before the head icons. Clicking it opens a
// picker anchored to it (a bottom sheet on a phone): Me first, then the devs, then everyone else with their
// role, a search box over 8 people, ticks on the people who have it now. Tick and untick, then Save: the
// whole change is one action with one history line. Taking a free item yourself and giving back your own
// stay one tap. A change that takes someone else off (handing it over, Unassign) asks first, with an
// optional note for the next person (the same credential guard as every note). Leads and devs change who
// has any item; anyone else, an item they're on (canAssignItem); others can only take it (claim).

import { assigneesOf, can, canAssignItem, nameList, type BoardAssignee, type BoardItem, type BoardMember, type Viewer } from "@boem/core/shared";
import { useMemo, useRef, useState, type KeyboardEvent } from "react";
import { initials, toneOf } from "../util.tsx";
import { Icon } from "./Icons.tsx";
import { Popover } from "./Menu.tsx";
import { SecretHint, useSecretCheck } from "./Modal.tsx";

/** "10/02": short, for a tooltip. */
export const shortDate = (iso: string | null | undefined) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}`;
};

/** "With Vishal since 10/02 · assigned by Kal" (or "· took it" when they claimed it themselves). */
export function assigneeTip(a: BoardItem["assignee"]): string {
  if (!a) return "Nobody has it";
  const since = shortDate(a.at);
  return `With ${a.name}${since ? ` since ${since}` : ""}${a.by ? (a.by === a.name ? " · took it" : ` · assigned by ${a.by}`) : ""}`;
}

/** Everyone an item is with, for a tooltip: one person as assigneeTip; several as "With Rohit and Vishal · Rohit since 10/02, Vishal since 10/05". */
export function assigneesTip(people: BoardAssignee[]): string {
  if (people.length <= 1) return assigneeTip(people[0] ?? null);
  const since = people.map((p) => `${p.name}${p.at ? ` since ${shortDate(p.at)}` : ""}`).join(", ");
  return `With ${nameList(people.map((p) => p.name))} · ${since}`;
}

/** A person's small avatar: 20px on a card's head, 16px (`sm`) in rows and filter chips. `me`: an accent ring. */
export function PersonAvatar({ name, sm = false, me = false }: { name: string; sm?: boolean; me?: boolean }) {
  return (
    <span className={`pav${sm ? " sm" : ""}${me ? " me" : ""}`} data-tone={toneOf(name)} aria-hidden="true">
      {initials(name)}
    </span>
  );
}

/** Nobody: a dashed empty circle with a person in it. */
export function NobodyAvatar({ sm = false }: { sm?: boolean }) {
  return (
    <span className={`pav nobody${sm ? " sm" : ""}`} aria-hidden="true">
      <Icon name="person" />
    </span>
  );
}

/** At most this many faces in a card's stack; the rest are "+n". */
export const FACES_MAX = 3;

/** The faces a stack shows and how many more: the lead first, up to `max` (the last slot goes to "+n" when there are more). Pure. */
export function stackFaces<P>(people: P[], max = FACES_MAX): { faces: P[]; more: number } {
  if (people.length <= max) return { faces: people, more: 0 };
  return { faces: people.slice(0, max - 1), more: people.length - (max - 1) };
}

/** Everyone an item is with as overlapping avatars (the lead first), up to 3 then "+n". */
export function AvatarStack({ people, viewerId, sm = false, max = FACES_MAX }: { people: { id?: string; name: string }[]; viewerId?: string | null; sm?: boolean; max?: number }) {
  const { faces, more } = stackFaces(people, max);
  return (
    <span className={`pav-stack${sm ? " sm" : ""}`} aria-hidden="true">
      {faces.map((p) => (
        <PersonAvatar key={p.id ?? p.name} name={p.name} sm={sm} me={!!viewerId && p.id === viewerId} />
      ))}
      {more > 0 && <span className={`pav pav-more${sm ? " sm" : ""}`}>+{more}</span>}
    </span>
  );
}

/** Read-only 16px avatars for a row (sub-items, links): everyone it's with, with the same hover text as the card's. */
export function RowAvatar({ name, tip, me = false, people }: { name: string; tip: string; me?: boolean; people?: { id?: string; name: string }[]; viewerId?: string }) {
  return (
    <span className="row-av" role="img" title={tip} aria-label={tip}>
      {people && people.length > 1 ? <AvatarStack people={people} sm /> : <PersonAvatar name={name} sm me={me} />}
    </span>
  );
}

/** A row's avatars for an item: everyone it's with (the viewer ringed when it's only them), or nothing. */
export function RowAvatars({ it, viewerId }: { it: Pick<BoardItem, "assignee" | "assignees">; viewerId?: string | null }) {
  const people = assigneesOf(it);
  if (!people.length) return null;
  const tip = assigneesTip(people);
  return (
    <span className="row-av" role="img" title={tip} aria-label={tip}>
      {people.length > 1 ? <AvatarStack people={people} viewerId={viewerId} sm /> : <PersonAvatar name={people[0]!.name} sm me={people[0]!.id === viewerId} />}
    </span>
  );
}

// -- What the picker offers --------------------------------------------------------------

export interface PickerModel {
  /** edit: tick anyone (leads, devs, the people on it); self: only take it yourself; none: it is with someone else */
  mode: "edit" | "self" | "none";
  me: BoardMember | null;
  /** Me's row: "Me" while editing; "Take it myself" or "Give it back" otherwise */
  meLabel: "Me" | "Take it myself" | "Give it back";
  /** why "Take it myself" can't be used yet (closed, in the archive) */
  meOff?: string;
  devs: BoardMember[];
  others: BoardMember[];
  /** who has it now, the lead first (member ids) */
  current: string[];
  search: boolean;
  /** a line under the list (or instead of it) */
  note?: string;
}

/** More people than this and the picker gets a search box. */
export const SEARCH_FROM = 8;

export function pickerModel(it: BoardItem, viewer: Viewer, members: BoardMember[]): PickerModel {
  const people = members.filter((m) => !m.isBot);
  const me = people.find((m) => m.id === viewer.id) ?? null;
  const on = assigneesOf(it);
  const cur = on.map((a) => a.id);
  const mine = cur.includes(viewer.id);
  const open = !it.archived && it.status === "open";
  const base = { me, current: cur, devs: [], others: [], search: false };
  if (!canAssignItem(viewer, cur)) {
    if (!can(viewer, "claim")) return { ...base, mode: "none", me: null, meLabel: "Take it myself", note: on.length ? `With ${nameList(on.map((a) => a.name))}.` : "Nobody has it." };
    const meOff = !open ? (it.archived ? "It's in the archive: a lead restores it first" : "It's closed: reopen it first") : undefined;
    const others = on.length ? `With ${nameList(on.map((a) => a.name))}. You can join them; ` : "";
    return {
      ...base,
      mode: "self",
      meLabel: "Take it myself",
      ...(meOff ? { meOff } : {}),
      note: `${others}${others ? "the" : "The"} people on it, developers and leads can hand it to someone else.`,
    };
  }
  const devs = people.filter((m) => m.role === "dev" && m.id !== viewer.id);
  const others = people.filter((m) => m.role !== "dev" && m.id !== viewer.id);
  return { ...base, mode: "edit", meLabel: "Me", devs, others, search: people.length > SEARCH_FROM, ...(mine && !can(viewer, "assign") ? { note: "You're on it, so you can change who has it." } : {}) };
}

/** What a change in the picker does. */
export interface PickOutcome {
  action: "claim" | "unclaim" | "set" | "none";
  /** it takes someone else off (a hand-over, Unassign): ask first */
  confirm: boolean;
  added: string[];
  removed: string[];
}

/**
 * What saving the picker's ticks does (`sel`: member ids, in the order ticked). Taking it yourself is a
 * claim and giving yours back is unclaim (as before); anything else is one change of people (`set`),
 * which asks first when it takes someone else off.
 */
export function pickOutcome(it: BoardItem, viewer: Viewer, sel: string[]): PickOutcome {
  const cur = assigneesOf(it).map((a) => a.id);
  const added = sel.filter((id) => !cur.includes(id));
  const removed = cur.filter((id) => !sel.includes(id));
  const open = !it.archived && it.status === "open";
  const none = { action: "none" as const, confirm: false, added, removed };
  if (!added.length && !removed.length) return none;
  const onlyMe = (xs: string[]) => xs.length === 1 && xs[0] === viewer.id;
  if (onlyMe(added) && !removed.length && open && can(viewer, "claim")) return { action: "claim", confirm: false, added, removed };
  if (onlyMe(removed) && !added.length && can(viewer, "claim")) return { action: "unclaim", confirm: false, added, removed };
  if (!canAssignItem(viewer, cur)) return none;
  return { action: "set", confirm: removed.some((id) => id !== viewer.id), added, removed };
}

/**
 * The confirm step for a change that takes someone off: "Hand 03-01 from Rohit to Vishal?", "Take Rohit
 * off 03-01?", "Unassign 03-01 from Rohit and Vishal?", with an optional note, Confirm / Cancel.
 */
export function AssignConfirm({
  label,
  removed,
  added,
  stay,
  note,
  onNote,
  busy = false,
  onConfirm,
  onCancel,
}: {
  label: string;
  /** names taken off */
  removed: string[];
  /** names put on */
  added: string[];
  /** names who keep it */
  stay: string[];
  note: string;
  onNote: (s: string) => void;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const secrets = useSecretCheck(note);
  const blocked = busy || secrets.length > 0;
  const nobody = !added.length && !stay.length;
  const still = stay.length ? <> It stays with <b>{nameList(stay)}</b>.</> : null;
  const [question, ok] = nobody
    ? [
        <>
          Unassign <b>{label}</b> from <b>{nameList(removed)}</b>?
        </>,
        "Unassign",
      ]
    : added.length
      ? [
          <>
            Hand <b>{label}</b> from <b>{nameList(removed)}</b> to <b>{nameList(added)}</b>?{still}
          </>,
          "Hand over",
        ]
      : [
          <>
            Take <b>{nameList(removed)}</b> off <b>{label}</b>?{still}
          </>,
          "Take off",
        ];
  return (
    <div className="pk-confirm">
      <p className="pk-q">{question}</p>
      <label htmlFor={`pk-why-${label}`}>{added.length ? "Why, and what the next step is for them" : "Why (optional)"}</label>
      <textarea
        id={`pk-why-${label}`}
        className="pk-why"
        rows={3}
        value={note}
        placeholder={added.length ? "e.g. Reported fixed 09/24 - reprint an invoice to verify, then close" : "e.g. Nobody is on it this week"}
        onChange={(e) => onNote(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && (e.metaKey || e.ctrlKey) && !blocked && onConfirm()}
      />
      <SecretHint findings={secrets} />
      <div className="row">
        <button type="button" className="btn" onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className="btn primary pk-ok" disabled={blocked} onClick={onConfirm}>
          {ok}
        </button>
      </div>
    </div>
  );
}

/**
 * The picker's list. Editing: Me, the devs, everyone else (search over 8 people) as ticks, then Save and
 * Unassign all; `sel` is what's ticked now. Self: one row, Take it myself. None: who has it.
 */
export function PickerList({
  model,
  sel,
  onToggle,
  onSave,
  onClear,
  onTake,
  label,
  busy = false,
}: {
  model: PickerModel;
  sel: string[];
  onToggle: (id: string) => void;
  onSave: () => void;
  onClear: () => void;
  /** self mode: take it yourself */
  onTake: () => void;
  label: string;
  busy?: boolean;
}) {
  const [q, setQ] = useState("");
  const find = q.trim().toLowerCase();
  const fits = (m: BoardMember) => !find || m.name.toLowerCase().includes(find) || m.role.includes(find);
  if (model.mode === "none") return <p className="pk-note">{model.note}</p>;
  if (model.mode === "self") {
    return (
      <>
        {model.me && (
          <div role="menu" aria-label={`Who has ${label}`}>
            <button type="button" className="pk-row" role="menuitem" aria-disabled={model.meOff ? "true" : undefined} title={model.meOff} onClick={() => !model.meOff && onTake()}>
              <PersonAvatar name={model.me.name} me />
              <span className="nm">
                {model.meLabel}
                <span className="rl">{model.me.name}</span>
              </span>
            </button>
          </div>
        )}
        {model.note && <p className="pk-note">{model.note}</p>}
      </>
    );
  }
  const picked = new Set(sel);
  const changed = sel.length !== model.current.length || sel.some((id) => !model.current.includes(id));
  const row = (m: BoardMember, text: string, role?: string) => (
    <button key={m.id} type="button" className="pk-row" role="menuitemcheckbox" aria-checked={picked.has(m.id)} hidden={!fits(m)} onClick={() => onToggle(m.id)}>
      <span className="af-box" aria-hidden="true">
        {picked.has(m.id) && <Icon name="check" />}
      </span>
      <PersonAvatar name={m.name} me={m.id === model.me?.id} />
      <span className="nm">
        {text}
        {role && <span className="rl">{role}</span>}
      </span>
      {model.current.includes(m.id) && <span className="pk-on">on it</span>}
    </button>
  );
  const sec = (title: string, xs: BoardMember[], text: (m: BoardMember) => [string, string?]) =>
    xs.length > 0 && (
      <div role="group" aria-label={title} hidden={!xs.some(fits)}>
        <div className="pk-sec" aria-hidden="true">
          {title}
        </div>
        {xs.map((m) => row(m, ...text(m)))}
      </div>
    );
  const visible = [...(model.me ? [model.me] : []), ...model.devs, ...model.others].filter(fits);
  return (
    <>
      {model.search && (
        <input
          type="search"
          className="pk-search"
          placeholder="Find a person…"
          aria-label="Find a person"
          autoComplete="off"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            // Enter ticks the only match
            if (e.key === "Enter" && visible.length === 1) {
              e.preventDefault();
              onToggle(visible[0]!.id);
            }
          }}
        />
      )}
      <div role="menu" aria-label={`Who has ${label}`}>
        {model.me && (
          <div role="group" aria-label="Me" hidden={!fits(model.me)}>
            <div className="pk-sec" aria-hidden="true">
              Me
            </div>
            {row(model.me, model.me.name, "me")}
          </div>
        )}
        {sec("Devs", model.devs, (m) => [m.name])}
        {sec("Everyone else", model.others, (m) => [m.name, m.role])}
      </div>
      {model.note && <p className="pk-note">{model.note}</p>}
      <div className="pk-foot">
        <span className="pk-count">{sel.length ? `${sel.length} ${sel.length === 1 ? "person" : "people"}` : "Nobody"}</span>
        {model.current.length > 0 && (
          <button type="button" className="btn pk-un" disabled={busy} onClick={onClear}>
            Unassign all
          </button>
        )}
        <button type="button" className="btn primary pk-save" disabled={busy || !changed} onClick={onSave}>
          Save
        </button>
      </div>
    </>
  );
}

// -- Who did the work: the stage dialog's picker for steps nobody marked -----------------

export interface DoneByModel {
  devs: BoardMember[];
  others: BoardMember[];
  /** who to start on: whoever has it, else the last person who claimed it (null: nobody yet) */
  preselect: string | null;
  search: boolean;
}

/**
 * Who could have developed or deployed an item nobody marked: the people on the team (no AI),
 * devs first, then everyone else. It starts on whoever has the item, else on the last person
 * who claimed it (a claim is a handoff they made to themselves).
 */
export function doneByModel(it: BoardItem, members: BoardMember[]): DoneByModel {
  const people = members.filter((m) => !m.isBot);
  const byName = (name: string | null) => (name ? (people.find((m) => m.name === name)?.id ?? null) : null);
  const claimed = [...it.handoffs].reverse().find((h) => h.to && h.to === h.by);
  const assignee = it.assignee && people.some((m) => m.id === it.assignee!.id) ? it.assignee.id : null;
  return {
    devs: people.filter((m) => m.role === "dev"),
    others: people.filter((m) => m.role !== "dev"),
    preselect: assignee ?? byName(claimed?.to ?? null),
    search: people.length > SEARCH_FROM,
  };
}

/** "Who developed and deployed it?": a person, required. Same rows as the assignee picker, as a radio group. */
export function DoneByPicker({
  model,
  value,
  onChange,
  label,
  viewerId,
}: {
  model: DoneByModel;
  value: string | null;
  onChange: (id: string) => void;
  /** the question, for the group's accessible name */
  label: string;
  viewerId: string;
}) {
  const [q, setQ] = useState("");
  const find = q.trim().toLowerCase();
  const fits = (m: BoardMember) => !find || m.name.toLowerCase().includes(find) || m.role.includes(find);
  const row = (m: BoardMember, role?: string) => (
    <button
      key={m.id}
      type="button"
      className="pk-row"
      role="radio"
      data-id={m.id}
      aria-checked={value === m.id}
      tabIndex={value === m.id || (!value && m === (model.devs[0] ?? model.others[0])) ? 0 : -1}
      hidden={!fits(m)}
      onClick={() => onChange(m.id)}
    >
      <PersonAvatar name={m.name} me={m.id === viewerId} />
      <span className="nm">
        {m.name}
        {m.id === viewerId && <span className="rl">you</span>}
        {role && m.id !== viewerId && <span className="rl">{role}</span>}
      </span>
      {value === m.id && <Icon name="check" className="tick" />}
    </button>
  );
  const all = [...model.devs, ...model.others];
  // arrow keys move along the visible rows and pick, as in any radio group
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!["ArrowDown", "ArrowUp", "ArrowRight", "ArrowLeft"].includes(e.key)) return;
    const shown = all.filter(fits);
    if (!shown.length) return;
    e.preventDefault();
    const at = shown.findIndex((m) => m.id === value);
    const next = shown[(at + (e.key === "ArrowDown" || e.key === "ArrowRight" ? 1 : shown.length - 1)) % shown.length]!;
    onChange(next.id);
    e.currentTarget.querySelector<HTMLElement>(`[data-id="${next.id}"]`)?.focus();
  };
  const sec = (title: string, xs: BoardMember[], roles: boolean) =>
    xs.length > 0 && (
      <div role="group" aria-label={title} hidden={!xs.some(fits)}>
        <div className="pk-sec" aria-hidden="true">
          {title}
        </div>
        {xs.map((m) => row(m, roles ? m.role : undefined))}
      </div>
    );
  return (
    <>
      {model.search && (
        <input
          type="search"
          className="pk-search"
          placeholder="Find a person…"
          aria-label="Find a person"
          autoComplete="off"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      )}
      <div className="db-list" role="radiogroup" aria-label={label} aria-required="true" onKeyDown={onKey}>
        {sec("Devs", model.devs, false)}
        {sec("Everyone else", model.others, true)}
      </div>
    </>
  );
}

/** What the card's picker asks the view to do: take it, give it back, or one change of people. */
export type AssignAct = { kind: "claim" } | { kind: "unclaim" } | { kind: "assignTo"; ids: string[]; note?: string };

/**
 * The avatars on a card's head and its picker. `onAct` runs claim / unclaim / assignTo through the card's
 * actions (so the board refreshes, and a refusal shows as a toast). `open` lets the "…" menu's Assign…
 * open the same picker.
 */
export function AssigneeButton({
  item,
  viewer,
  members,
  label,
  title,
  onAct,
  open,
  setOpen,
}: {
  item: BoardItem;
  viewer: Viewer;
  members: BoardMember[];
  /** how the card names itself: "03-01" */
  label: string;
  title: string;
  onAct: (a: AssignAct) => unknown;
  open: boolean;
  setOpen: (on: boolean) => void;
}) {
  const btn = useRef<HTMLButtonElement>(null);
  const people = assigneesOf(item);
  const model = useMemo(() => pickerModel(item, viewer, members), [item, viewer, members]);
  const [sel, setSel] = useState<string[]>(model.current);
  const [step, setStep] = useState<{ ids: string[]; note: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const tip = people.length ? assigneesTip(people) : canAssignItem(viewer, []) ? "Nobody has it: assign it" : "Nobody has it: take it";
  const close = () => {
    setOpen(false);
    setStep(null);
  };
  const show = () => {
    setStep(null);
    setSel(model.current);
    setOpen(true);
  };
  const run = async (x: AssignAct) => {
    setBusy(true);
    try {
      await onAct(x);
    } finally {
      setBusy(false);
      close();
      btn.current?.focus();
    }
  };
  const save = (ids: string[]) => {
    const o = pickOutcome(item, viewer, ids);
    if (o.action === "none") return close();
    if (o.action === "claim") return run({ kind: "claim" });
    if (o.action === "unclaim") return run({ kind: "unclaim" });
    if (o.confirm) return setStep({ ids, note: "" });
    return run({ kind: "assignTo", ids });
  };
  const nameOf = (id: string) => (id === viewer.id ? "you" : (members.find((m) => m.id === id)?.name ?? people.find((p) => p.id === id)?.name ?? "someone"));
  const confirm = step ? pickOutcome(item, viewer, step.ids) : null;
  return (
    <>
      <button
        ref={btn}
        type="button"
        className={`hi hav${people.length > 1 ? " many" : ""}`}
        data-tip={tip}
        aria-label={people.length ? `${tip}. Change who has it` : tip}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => (open ? close() : show())}
      >
        {people.length > 1 ? <AvatarStack people={people} viewerId={viewer.id} /> : people[0] ? <PersonAvatar name={people[0].name} me={people[0].id === viewer.id} /> : <NobodyAvatar />}
      </button>
      {open && (
        <Popover
          anchor={btn}
          onClose={close}
          label={`Who has ${label}`}
          role="dialog"
          className="apick"
          focus={step ? ".pk-why" : ".pk-search | .pk-row[aria-checked='true'] | .pk-row"}
          sheetHead={
            <>
              <b>{label}</b>
              {title}
            </>
          }
        >
          {step && confirm ? (
            <AssignConfirm
              label={label}
              removed={confirm.removed.map(nameOf)}
              added={confirm.added.map(nameOf)}
              stay={step.ids.filter((id) => !confirm.added.includes(id)).map(nameOf)}
              note={step.note}
              onNote={(note) => setStep({ ...step, note })}
              busy={busy}
              onCancel={() => setStep(null)}
              onConfirm={() => run({ kind: "assignTo", ids: step.ids, note: step.note.trim() || undefined })}
            />
          ) : (
            <PickerList
              model={model}
              sel={sel}
              onToggle={(id) => setSel((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]))}
              onSave={() => save(sel)}
              onClear={() => save([])}
              onTake={() => run({ kind: "claim" })}
              label={label}
              busy={busy}
            />
          )}
        </Popover>
      )}
    </>
  );
}
